/* Small local-time date helpers for the calendar view. Timestamps are ms
   epoch everywhere (the repo convention); rendering is the browser's locale
   and zone — the calendar service stores UTC and Google carries the zone. */

export const DAY_MS = 86_400_000
export const HOUR_MS = 3_600_000
export const MIN_MS = 60_000

export function startOfDay(t: number): number {
  const d = new Date(t)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

export function addDays(t: number, n: number): number {
  const d = new Date(t)
  d.setDate(d.getDate() + n)
  return d.getTime()
}

export function addMonths(t: number, n: number): number {
  const d = new Date(t)
  d.setDate(1)
  d.setMonth(d.getMonth() + n)
  return d.getTime()
}

/** Monday-first week start. */
export function startOfWeek(t: number): number {
  const d = new Date(startOfDay(t))
  const shift = (d.getDay() + 6) % 7
  return addDays(d.getTime(), -shift)
}

export function startOfMonth(t: number): number {
  const d = new Date(t)
  d.setDate(1)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

export function isSameDay(a: number, b: number): boolean {
  return startOfDay(a) === startOfDay(b)
}

export function isToday(t: number): boolean {
  return isSameDay(t, Date.now())
}

/** Minutes into the local day. */
export function minutesOfDay(t: number): number {
  const d = new Date(t)
  return d.getHours() * 60 + d.getMinutes()
}

export function fmtTime(t: number): string {
  return new Date(t).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

export function fmtDayShort(t: number): string {
  return new Date(t).toLocaleDateString(undefined, { weekday: 'short' })
}

export function fmtDayLong(t: number): string {
  return new Date(t).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
}

export function fmtMonthYear(t: number): string {
  return new Date(t).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
}

export function fmtDate(t: number): string {
  return new Date(t).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

/** '9 – 15 Jun 2026' style range title for a week. */
export function fmtWeekTitle(weekStart: number): string {
  const end = addDays(weekStart, 6)
  const s = new Date(weekStart)
  const e = new Date(end)
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()
  const left = s.toLocaleDateString(undefined, sameMonth ? { day: 'numeric' } : { day: 'numeric', month: 'short' })
  const right = e.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  return `${left} – ${right}`
}

/** 'YYYY-MM-DD' for <input type="date"> in local time. */
export function toDateInput(t: number): string {
  const d = new Date(t)
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/** 'HH:MM' for <input type="time"> in local time. */
export function toTimeInput(t: number): string {
  const d = new Date(t)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** Local date ('YYYY-MM-DD') + time ('HH:MM') → ms epoch. */
export function fromInputs(date: string, time: string): number {
  const [y, m, d] = date.split('-').map(Number)
  const [hh, mm] = (time || '00:00').split(':').map(Number)
  return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0).getTime()
}

/** Local date ('YYYY-MM-DD') → UTC midnight ms (Google's all-day convention). */
export function dateInputToUTC(date: string): number {
  const [y, m, d] = date.split('-').map(Number)
  return Date.UTC(y, (m || 1) - 1, d || 1)
}

/** UTC midnight ms → the same civil day's LOCAL midnight ms. All-day events
    travel as UTC dates; grids and labels want local day bounds. */
export function utcDayToLocal(t: number): number {
  const d = new Date(t)
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()).getTime()
}

/** Display bounds for an event: all-day spans mapped onto local days. */
export function eventBounds(ev: { start: number; end: number; all_day?: boolean }): { start: number; end: number } {
  if (!ev.all_day) return { start: ev.start, end: ev.end }
  return { start: utcDayToLocal(ev.start), end: utcDayToLocal(ev.end) }
}

/** UTC midnight ms → local 'YYYY-MM-DD' (inverse of dateInputToUTC). */
export function utcToDateInput(t: number): string {
  const d = new Date(t)
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${d.getUTCFullYear()}-${m}-${day}`
}

/** Human duration between two instants: '45 min', '1½ h', '2 h'. */
export function fmtDuration(from: number, to: number): string {
  const mins = Math.max(0, Math.round((to - from) / MIN_MS))
  if (mins < 60) return `${mins} min`
  const h = Math.floor(mins / 60)
  const rem = mins % 60
  if (rem === 0) return `${h} h`
  if (rem === 30) return `${h}½ h`
  return `${h} h ${rem} min`
}
